import {
  areRangesOverlapping,
  differenceInMinutes,
  eachDay,
  endOfDay,
  getDay,
  isToday,
  isWeekend,
  startOfDay
} from 'date-fns';
import { Day, IViewMode, IViewModeColumn, MediaItem, TimelineDataItem, TreeItem } from '../store';
import { ViewModeType } from '../store/types';
import * as Utils from './utils';

export class DaysViewMode implements IViewMode {
  public type = ViewModeType.Days;
  public columns: IViewModeColumn[];
  constructor() {
    this.computeColumns = this.computeColumns.bind(this);
  }

  public computeColumns = (startDate: Date, endDate: Date) => {
    startDate = startOfDay(startDate);
    endDate = endOfDay(endDate);
    const daysInRange = eachDay(startDate, endDate);

    const largeHeaders = daysInRange.length <= 15;
    const width = largeHeaders ? 100 : 37;
    const headerTestClassModifier = largeHeaders ? 'days' : 'daysCompact';

    this.columns = daysInRange.map<IViewModeColumn>(date => ({
      startDate: startOfDay(date),
      endDate: endOfDay(date),
      title: date.getDate().toString(),
      subtitle: Utils.nameOfTheDay(getDay(date)).toLowerCase(),
      ...this.isColumnNow(date, width),
      width,
      headerTitleClassModifier: headerTestClassModifier,
      headerSubtitleClassModifier: headerTestClassModifier,
      contentCellClassModifier: isWeekend(date) ? ' weekend' : '',
      headerCellClassModifier: isWeekend(date) ? ' weekend' : ''
    }));
  };

  public applyToMedia = (media: MediaItem, treeItem: TreeItem): TimelineDataItem[] => {
    // if the media has no days allocated then display nothing
    if (media.diffusionDays === Day.None) {
      return [];
    }

    // check the days in which the media is playing
    const daysInPlay = Utils.daysForDiffusionFlag(media.diffusionDays);
    if (daysInPlay.length === 0) {
      return [];
    }

    // compute the range of the timeline bars in normalized coordinates for each column
    // this view mode will have only one bar per column
    return this.columns.map((c, i) => {
      if (daysInPlay.indexOf(c.startDate.getDay()) === -1) {
        return {
          colIndex: i,
          mediaType: media.mediaType,
          durations: []
        };
      }

      // if the media date range overlaps with the column the set the duration as 100%
      if (areRangesOverlapping(c.startDate, c.endDate, media.startDate, media.endDate)) {
        return {
          colIndex: i,
          mediaType: media.mediaType,
          durations: [{ start: 0, end: 1, insideValidRange: this.isInsideValidRange(media, treeItem, c) }]
        };
      }

      // by default we return no durations for the column
      return {
        colIndex: i,
        mediaType: media.mediaType,
        durations: [],
        insideValidRange: true
      };
    });
  };

  private isColumnNow = (date: Date, width: number) => {
    const includesNow = isToday(date);
    if (!includesNow) {
      return { includesNow, nowPosition: -1 };
    }
    const beginningOfToday = startOfDay(new Date());
    const now = new Date();
    const nowPosition = (differenceInMinutes(now, beginningOfToday) / 1440) * width; // 1440 - number of minutes in a day
    return { includesNow, nowPosition };
  };

  private isInsideValidRange = (media: MediaItem, treeItem: TreeItem, c: IViewModeColumn) => {
    if (!(treeItem.startDate && treeItem.endDate)) {
      return false;
    }

    let insideValidRange = areRangesOverlapping(treeItem.startDate, treeItem.endDate, c.startDate, c.endDate);
    const parentDaysInPlay = Utils.daysForDiffusionFlag(treeItem.diffusionDays);

    if (parentDaysInPlay.indexOf(c.startDate.getDay()) === -1) {
      insideValidRange = false;
    }
    return insideValidRange;
  };
}



// WEBPACK FOOTER //
// ./src/viewModes/DaysViewMode.ts